import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import {
  Activity, Users, Clock, Filter, ChevronLeft, ChevronRight,
  LogIn, Monitor, UtensilsCrossed, ShoppingCart, Calculator,
  Truck, QrCode, Settings as SettingsIcon, UserPlus, UserMinus, Pencil,
  RefreshCw, TrendingUp
} from 'lucide-react';

const ROLE_COLORS = {
  owner: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  manager: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  cashier: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  chef: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  accountant: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
};

const ACTION_ICONS = {
  'auth.login': LogIn,
  'shift.open': Monitor, 'shift.close': Monitor,
  'pos.order.create': Monitor,
  'menu.item.create': UtensilsCrossed, 'menu.item.edit': Pencil, 'menu.item.delete': UtensilsCrossed,
  'order.create': ShoppingCart, 'order.status.update': ShoppingCart,
  'team.invite': UserPlus, 'team.update': Pencil, 'team.remove': UserMinus,
  'expense.create': Calculator, 'expense.edit': Pencil, 'expense.delete': Calculator,
  'table.create': QrCode, 'table.delete': QrCode,
  'driver.create': Truck, 'driver.update': Pencil, 'driver.delete': Truck,
  'branch.create': SettingsIcon, 'branch.update': SettingsIcon,
  'settings.update': SettingsIcon,
  'subscription.upgrade': TrendingUp,
};

const CATEGORY_COLORS = {
  auth: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  pos: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  menu: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  orders: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  team: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  accounting: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  tables: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  fleet: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  settings: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

function timeAgo(dateStr, lang) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return lang === 'ar' ? 'الآن' : 'Just now';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return lang === 'ar' ? `منذ ${m} دقيقة` : `${m}m ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return lang === 'ar' ? `منذ ${h} ساعة` : `${h}h ago`;
  }
  const d = Math.floor(diff / 86400);
  return lang === 'ar' ? `منذ ${d} يوم` : `${d}d ago`;
}

export default function ActivityLogPage() {
  const { authHeaders, API, activeBranch } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [labels, setLabels] = useState({ actions: {}, categories: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ category: '', user_id: '' });
  const [teamMembers, setTeamMembers] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const branchId = activeBranch?.id || '';
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '30' });
      if (branchId) params.set('branch_id', branchId);
      if (filters.category) params.set('category', filters.category);
      if (filters.user_id) params.set('user_id', filters.user_id);

      const [logRes, statsRes, labelsRes] = await Promise.all([
        axios.get(`${API}/activity-log?${params}`, authHeaders()),
        axios.get(`${API}/activity-log/stats${branchId ? '?branch_id=' + branchId : ''}`, authHeaders()),
        axios.get(`${API}/activity-log/labels`, authHeaders()),
      ]);
      setEntries(logRes.data.entries || []);
      setTotalPages(logRes.data.pages || 1);
      setStats(statsRes.data);
      setLabels(labelsRes.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [page, filters, activeBranch, API, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    axios.get(`${API}/team`, authHeaders())
      .then(res => setTeamMembers(res.data.members || []))
      .catch(() => {});
  }, [API, authHeaders]);

  const getActionLabel = (action) => {
    const label = labels.actions?.[action];
    if (label) return lang === 'ar' ? label.ar : label.en;
    return action;
  };

  const getCategoryLabel = (cat) => {
    const label = labels.categories?.[cat];
    if (label) return lang === 'ar' ? label.ar : label.en;
    return cat;
  };

  const ActionIcon = ({ action }) => {
    const Icon = ACTION_ICONS[action] || Activity;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <ModuleLayout moduleKey="settings">
      <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto" data-testid="activity-log-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="activity-log-title">
              <Activity className="w-6 h-6 text-primary" />
              {t('activityLog.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('activityLog.subtitle')}</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-2" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
            {t('activityLog.refresh')}
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="border-border/50">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-primary" data-testid="stat-today">{stats.today}</div>
                <div className="text-xs text-muted-foreground mt-1">{t('activityLog.today')}</div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400" data-testid="stat-week">{stats.week}</div>
                <div className="text-xs text-muted-foreground mt-1">{t('activityLog.thisWeek')}</div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="stat-active-users">
                  {stats.active_users_today?.length || 0}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{t('activityLog.activeUsers')}</div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-amber-600 dark:text-amber-400" data-testid="stat-total">{stats.total}</div>
                <div className="text-xs text-muted-foreground mt-1">{t('activityLog.total')}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={filters.category} onValueChange={v => { setFilters(f => ({ ...f, category: v === 'all' ? '' : v })); setPage(1); }}>
            <SelectTrigger className="w-40" data-testid="filter-category">
              <SelectValue placeholder={t('activityLog.allCategories')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('activityLog.allCategories')}</SelectItem>
              {Object.keys(labels.categories || {}).map(cat => (
                <SelectItem key={cat} value={cat}>{getCategoryLabel(cat)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.user_id} onValueChange={v => { setFilters(f => ({ ...f, user_id: v === 'all' ? '' : v })); setPage(1); }}>
            <SelectTrigger className="w-44" data-testid="filter-user">
              <SelectValue placeholder={t('activityLog.allMembers')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('activityLog.allMembers')}</SelectItem>
              {teamMembers.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Activity Timeline */}
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {t('activityLog.timeline')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12" data-testid="no-activities">
                <Activity className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="font-medium text-muted-foreground">{t('activityLog.noActivities')}</p>
              </div>
            ) : (
              <div className="space-y-1">
                {entries.map((entry, idx) => (
                  <div key={entry.id}
                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/30 transition-colors group"
                    data-testid={`activity-entry-${idx}`}>
                    {/* Icon */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${CATEGORY_COLORS[entry.category] || 'bg-muted'}`}>
                      <ActionIcon action={entry.action} />
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-sm">{entry.user_name}</span>
                        <Badge className={`text-[10px] px-1.5 py-0 ${ROLE_COLORS[entry.user_role] || ''}`}>
                          {t(`team.roles.${entry.user_role}`, entry.user_role)}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {getActionLabel(entry.action)}
                        </span>
                      </div>
                      {entry.details && (
                        <p className="text-sm text-muted-foreground/80 mt-0.5 truncate">{entry.details}</p>
                      )}
                    </div>
                    {/* Time */}
                    <div className="text-xs text-muted-foreground/60 shrink-0 mt-1">
                      {timeAgo(entry.created_at, lang)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-border/30">
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                  <ChevronRight className="w-4 h-4 rtl:rotate-180" />
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  {page} / {totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)} data-testid="next-page">
                  <ChevronLeft className="w-4 h-4 rtl:rotate-180" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ModuleLayout>
  );
}
